/**
 * As notificações internas (`R-08`, ADR-078): a caixa da equipe e o que foi aos sistemas.
 *
 * **Cada destinatário recebe pelo canal dele** (decisão de 22/10, no domínio): o Backoffice e a
 * Monitoria CDT aqui, na tela, e alguém dá *ciente*; a Sugestão de Autuação e a operadora da TAG
 * por webhook assinado, que o cockpit tenta por até 24 horas; e o aviso da CNH Digital, que a
 * SERPRO faz — o cockpit só registra.
 *
 * **A tela não decide nada**: se pode dar ciente, se pode reenviar, se está atrasada, o rótulo
 * de cada situação — tudo vem do servidor. Reenviar é da supervisão (o servidor confere e
 * grava na trilha); o ciente, de quem recebe.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router";

import { ErroDaApi, api } from "../api/cliente";
import { useSessao } from "../api/contexto-sessao";
import type {
  ContagemDasNotificacoes,
  FeitoNaNotificacao,
  NotificacaoInterna,
  NotificacoesInternas as Lista,
} from "../api/tipos";
import { Badge, Botao, Estado, Painel } from "../componentes/base";
import { Icone } from "../componentes/icones";
import { recebeNotificacoes, supervisiona } from "../componentes/navegacao";
import { dataEHora } from "../formato";
import {
  CAIXAS,
  type Caixa,
  corDaSituacao,
  linhaDaEntrega,
  oQueEspera,
} from "./notificacoes";

function mensagem(falha: unknown, padrao: string): string {
  return falha instanceof ErroDaApi ? falha.message : padrao;
}

const ROTULO_DO_MODO: Record<string, string> = {
  configurado: "",
  emulador: "o emulador da demonstração (simulado)",
  sem_destino: "sem endereço cadastrado",
};

export function NotificacoesInternas() {
  const { quem } = useSessao();
  const [caixa, definirCaixa] = useState<Caixa>("equipe");
  const [abertas, definirAbertas] = useState(true);
  const [pagina, definirPagina] = useState(1);
  const [lista, definirLista] = useState<Lista | null>(null);
  const [erro, definirErro] = useState<string | null>(null);
  const [aviso, definirAviso] = useState<string | null>(null);
  const [fazendo, definirFazendo] = useState<string | null>(null);

  const pode = recebeNotificacoes(quem?.perfil);

  const carregar = useCallback(async () => {
    const consulta = new URLSearchParams({
      caixa,
      abertas: String(abertas),
      pagina: String(pagina),
    });
    try {
      const lida = await api<Lista>(
        `/tollvision/notificacoes-internas?${consulta}`,
      );
      // O ciente da última da página a esvazia: volta uma, em vez de "página 3 de 2".
      if (lida.itens.length === 0 && pagina > 1) {
        definirPagina((p) => Math.max(1, p - 1));
        return;
      }
      definirLista(lida);
      definirErro(null);
    } catch (falha) {
      definirErro(mensagem(falha, "Não foi possível ler as notificações."));
    }
  }, [caixa, abertas, pagina]);

  useEffect(() => {
    if (pode) void carregar();
  }, [carregar, pode]);

  if (!pode) {
    return (
      <Estado
        tipo="bloqueado"
        titulo="As notificações são da equipe que as recebe."
        detalhe="O supervisor, o backoffice e o administrador veem a caixa da equipe e o que foi aos sistemas."
      />
    );
  }

  const agir = async (n: NotificacaoInterna, acao: "ciente" | "reenviar") => {
    definirFazendo(n.id);
    try {
      const feito = await api<FeitoNaNotificacao>(
        `/tollvision/notificacoes-internas/${n.id}/${acao}`,
        { method: "POST" },
      );
      definirAviso(
        acao === "ciente"
          ? `Ciente dado: ${n.evento_rotulo.toLowerCase()}.`
          : `Reenviada: ${feito.situacao_rotulo.toLowerCase()} — sai na próxima rodada.`,
      );
      await carregar();
    } catch (falha) {
      definirAviso(mensagem(falha, "Não foi possível."));
    } finally {
      definirFazendo(null);
    }
  };

  const contagem = lista?.contagem;
  const naCaixa: Record<Caixa, number> = {
    equipe: contagem?.equipe_sem_ciente ?? 0,
    sistemas:
      (contagem?.sistemas_nao_entregues ?? 0) +
      (contagem?.sistemas_sem_destino ?? 0),
    serpro: 0,
  };
  const atual = CAIXAS.find((c) => c.chave === caixa)!;
  const paginas = lista
    ? Math.max(1, Math.ceil(lista.total / lista.por_pagina))
    : 1;

  return (
    <>
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <h1 className="text-titulo-tela leading-none font-extrabold tracking-[-0.01em] text-tinta uppercase">
            Notificações
          </h1>
          <p className="text-subtitulo-tela text-tinta-3">
            O que o cockpit avisou à equipe e aos sistemas, e se chegou
          </p>
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {contagem && contagem.atrasadas > 0 && (
            <Badge cor="vermelho">
              {contagem.atrasadas}{" "}
              {contagem.atrasadas === 1 ? "atrasada" : "atrasadas"}
            </Badge>
          )}
          <Link
            to="/senatran"
            className="inline-flex min-h-8 items-center gap-1.5 rounded-botao border border-linha bg-painel-2 px-2.5 text-[11.5px] font-semibold text-tinta-2 hover:bg-realce"
          >
            ‹ Voltar à SENATRAN
          </Link>
        </div>
      </div>

      <div
        role="tablist"
        aria-label="Caixas de notificação"
        className="flex flex-wrap items-center gap-1.5"
      >
        {CAIXAS.map((c) => (
          <button
            key={c.chave}
            type="button"
            role="tab"
            aria-selected={caixa === c.chave}
            onClick={() => {
              definirCaixa(c.chave);
              definirPagina(1);
              definirAviso(null);
            }}
            className={`inline-flex min-h-8 items-center gap-1.5 rounded-botao border px-2.5 text-[11.5px] font-semibold ${
              caixa === c.chave
                ? "border-azul-borda bg-realce text-tinta"
                : "border-linha bg-painel-2 text-tinta-2 hover:bg-realce"
            }`}
          >
            {c.rotulo}
            {naCaixa[c.chave] > 0 && (
              <span className="rounded-pilula bg-amarelo-fundo px-1.5 text-[10.5px] text-amarelo">
                {naCaixa[c.chave]}
              </span>
            )}
          </button>
        ))}
        <label className="ml-auto inline-flex items-center gap-1.5 text-[11px] text-tinta-3">
          <input
            type="checkbox"
            checked={abertas}
            onChange={(e) => {
              definirAbertas(e.target.checked);
              definirPagina(1);
            }}
          />
          Só o que ainda espera
        </label>
      </div>

      <p className="max-w-[110ch] text-[11.5px] text-tinta-3">
        {atual.explicacao}
      </p>

      {caixa === "sistemas" && lista && (
        <ul
          aria-label="Para onde cada sistema recebe"
          className="flex flex-wrap gap-2 text-[11.5px]"
        >
          {lista.sistemas.map((s) => (
            <li
              key={s.destinatario}
              className="inline-flex items-center gap-1.5 rounded-pilula border border-linha bg-painel-2 px-2.5 py-1 text-tinta-2"
            >
              <b className="text-tinta">{s.rotulo}</b>→
              {s.modo === "configurado" ? (
                <span className="mono">{s.servidor}</span>
              ) : (
                <span
                  className={
                    s.modo === "sem_destino" ? "text-vermelho" : "text-tinta-3"
                  }
                >
                  {ROTULO_DO_MODO[s.modo] ?? s.modo}
                </span>
              )}
            </li>
          ))}
          {quem?.perfil === "ADMIN" && (
            <li>
              <Link
                to="/administracao?secao=notificacoes"
                className="inline-flex min-h-7 items-center gap-1 text-[11.5px] font-semibold text-azul-claro hover:underline"
              >
                Cadastrar endereços
              </Link>
            </li>
          )}
        </ul>
      )}

      {aviso && (
        <p role="status" className="text-[11.5px] text-tinta-2">
          {aviso}
        </p>
      )}

      <Painel
        titulo={atual.rotulo}
        rolagemPropria
        acoes={
          <span className="text-[11px] text-tinta-3">
            {lista
              ? `${lista.total} ${lista.total === 1 ? "notificação" : "notificações"}`
              : ""}
          </span>
        }
        rodape={
          lista && paginas > 1 ? (
            <div className="flex items-center gap-2 text-[11px] text-tinta-3">
              <Botao
                disabled={pagina <= 1}
                onClick={() => definirPagina((p) => p - 1)}
              >
                Mais novas
              </Botao>
              Página {pagina} de {paginas}
              <Botao
                disabled={pagina >= paginas}
                onClick={() => definirPagina((p) => p + 1)}
              >
                Mais antigas
              </Botao>
            </div>
          ) : undefined
        }
      >
        {erro ? (
          <Estado tipo="erro" titulo={erro} />
        ) : lista === null ? (
          <Estado tipo="carregando" titulo="Lendo as notificações…" />
        ) : lista.itens.length === 0 ? (
          <Estado
            tipo="vazio"
            titulo={
              abertas
                ? "Nada esperando nesta caixa."
                : "Nenhuma notificação nesta caixa."
            }
            {...(abertas
              ? {
                  detalhe:
                    "Desmarque “Só o que ainda espera” para ver o que já chegou.",
                }
              : {})}
          />
        ) : (
          <ul className="grid gap-2">
            {lista.itens.map((n) => (
              <ItemDaCaixa
                key={n.id}
                n={n}
                fazendo={fazendo === n.id}
                podeReenviar={supervisiona(quem?.perfil) && n.pode_reenviar}
                aoAgir={(acao) => void agir(n, acao)}
              />
            ))}
          </ul>
        )}
      </Painel>
    </>
  );
}

/**
 * O link do cabeçalho da SENATRAN para a caixa, com o que espera alguém — a sem ciente, a que
 * não chegou ao sistema, a sem endereço. Lê uma vez, ao abrir a tela; erro não tira o link.
 */
export function LinkDasNotificacoes() {
  const { quem } = useSessao();
  const pode = recebeNotificacoes(quem?.perfil);
  const [esperando, definirEsperando] = useState(0);
  useEffect(() => {
    if (!pode) return;
    let vivo = true;
    api<ContagemDasNotificacoes>("/tollvision/notificacoes-internas/esperando")
      .then((r) => {
        if (vivo && r && typeof r.equipe_sem_ciente === "number")
          definirEsperando(oQueEspera(r));
      })
      .catch(() => undefined);
    return () => {
      vivo = false;
    };
  }, [pode]);
  if (!pode) return null;
  return (
    <Link
      to="/senatran/notificacoes"
      title="O que o cockpit avisou à equipe e aos sistemas — a Sugestão de Autuação, a operadora da TAG, o Backoffice e a Monitoria CDT —, e se chegou."
      className="inline-flex min-h-8 items-center gap-1.5 rounded-botao border border-linha bg-painel-2 px-2.5 text-[11.5px] font-semibold text-tinta-2 hover:bg-realce"
    >
      <Icone.Alerta />
      Notificações
      {esperando > 0 && (
        <span
          aria-label={`${esperando} esperando`}
          className="rounded-pilula bg-amarelo-fundo px-1.5 text-[10.5px] text-amarelo"
        >
          {esperando}
        </span>
      )}
    </Link>
  );
}

function ItemDaCaixa({
  n,
  fazendo,
  podeReenviar,
  aoAgir,
}: {
  n: NotificacaoInterna;
  fazendo: boolean;
  podeReenviar: boolean;
  aoAgir: (acao: "ciente" | "reenviar") => void;
}) {
  const entrega = linhaDaEntrega(n, dataEHora);
  return (
    <li
      aria-label={`${n.evento_rotulo} → ${n.destinatario_rotulo}`}
      className="grid gap-1 rounded-cartao border border-linha bg-painel-2 px-3 py-2"
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="mono text-[11px] text-tinta-3">
          {dataEHora(n.em ?? n.criado_em)}
        </span>
        <Badge cor={corDaSituacao(n.situacao)}>{n.situacao_rotulo}</Badge>
        {n.atrasada && <Badge cor="vermelho">atrasada</Badge>}
        {n.simulado && <Badge cor="cinza">simulado</Badge>}
        <span className="font-semibold text-tinta">{n.evento_rotulo}</span>
        <span className="text-tinta-3">→ {n.destinatario_rotulo}</span>
        <span className="ml-auto flex items-center gap-1.5">
          {n.placa && (
            <Link
              to={`/senatran?busca=${encodeURIComponent(n.placa)}`}
              className="mono text-[11.5px] text-azul-claro hover:underline"
            >
              {n.placa}
            </Link>
          )}
          {n.concessao && <Badge cor="cinza">{n.concessao}</Badge>}
          {n.pode_dar_ciente && (
            <Botao
              papel="aprovar"
              disabled={fazendo}
              onClick={() => aoAgir("ciente")}
            >
              <Icone.Certo />
              Ciente
            </Botao>
          )}
          {podeReenviar && (
            <Botao
              papel="atencao"
              disabled={fazendo}
              onClick={() => aoAgir("reenviar")}
            >
              <Icone.Enviar />
              Reenviar
            </Botao>
          )}
        </span>
      </div>
      {n.finalidade && (
        <p className="text-[11px] text-tinta-2">{n.finalidade}</p>
      )}
      {n.motivo_do_fato && (
        <p className="text-[11px] text-tinta-3">
          Justificativa: {n.motivo_do_fato}
        </p>
      )}
      {entrega && <p className="text-[11px] text-tinta-3">{entrega}</p>}
      {n.motivo && n.situacao !== "SEM_DESTINO" && (
        <p className="text-[11px] text-vermelho">Último motivo: {n.motivo}</p>
      )}
      {n.ciente_em && (
        <p className="text-[11px] text-tinta-3">
          Ciente de {n.ciente_por ?? "alguém da equipe"} às{" "}
          {dataEHora(n.ciente_em)}.
        </p>
      )}
      {n.reenviada_em && (
        <p className="text-[11px] text-tinta-3">
          Reenviada por {n.reenviada_por ?? "alguém da supervisão"} às{" "}
          {dataEHora(n.reenviada_em)}.
        </p>
      )}
    </li>
  );
}
